import {
    Box,
    Button,
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    FormControl,
    Select,
    MenuItem,
    TextField,
    SelectChangeEvent,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import * as locales from 'date-fns/locale';
import React, {useState, useEffect, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import i18next, {getBrowserLanguage} from '../../lang';
import ConfirmDialog from '../../components/ConfirmDialog';
import {timeSlots} from "./DeliveryDateTime";
import { addBusinessDays } from 'date-fns';
import {Category, Product, ProductSet} from "../admin/Product/Interfaces";
import {rootStore} from "../../stores/RootStore";
import axios from "axios";
import PurchaseStepper from "../../components/PurchaseStepper";
import { DeliveryData, OrderQuantity, UserProductMapping } from './interfaces';

interface LocationState {
    orderData: {
        deliveryData: DeliveryData[];
        orderQuantities: OrderQuantity[];
    };
    products: Product[],
    productSets: ProductSet[],
    categories: Category[],
    userProductMappings: { [key: string]: UserProductMapping[] },
    hasTempOrder?: boolean;
}

const OrderConfirm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { authStore, appConfigStore } = rootStore;
    const [isCancelOrderDialogOpen, setIsCancelOrderDialogOpen] = useState(false);
    const [isCompletedSaveTemporaryOrderDialogOpen, setIsCompletedSaveTemporaryOrderDialogOpen] = useState(false);
    const locationState = location.state as LocationState | null;
    const orderData = locationState?.orderData;
    const products = locationState?.products;
    const productSets = locationState?.productSets;
    const categories = locationState?.categories;
    const userProductMappings = locationState?.userProductMappings;
    const hasTempOrder = locationState?.hasTempOrder ?? false;
    const [selectedDeliveryIndex, setSelectedDeliveryIndex] = useState<number | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deliveryDates, setDeliveryDates] = useState<{ [key: number]: Date | null }>(
        () => orderData?.deliveryData?.reduce((acc, delivery) => ({
            ...acc,
            [delivery.id]: delivery.deliveryDate ? new Date(delivery.deliveryDate) : null
        }), {}) || {}
    );
    const [deliveryTimes, setDeliveryTimes] = useState<{ [key: number]: number }>(
        () => orderData?.deliveryData?.reduce((acc, delivery) => ({
            ...acc,
            [delivery.id]: delivery.deliveryTime || null
        }), {}) || {}
    );

    // 기존 주문 데이터와 새로운 주문 데이터를 합친 상태 관리
    const [orderDataState, setOrderDataState] = useState(() => {
        if (!orderData) return { deliveryData: [], orderQuantities: [] };
        // 중복된 배송지 제거
        const uniqueDeliveryData = orderData.deliveryData.reduce((acc, curr) => {
            const exists = acc.find(item => item.id === curr.id);
            if (!exists) {
                acc.push(curr);
            }
            return acc;
        }, [] as DeliveryData[]);

        // 중복된 주문 수량 데이터 병합
        const mergedOrderQuantities = orderData.orderQuantities.reduce((acc, curr) => {
            const existingCurriculum = acc.find(item =>
                item.name === curr.name && item.type === curr.type
            );

            if (existingCurriculum) {
                existingCurriculum.books = existingCurriculum.books.map(book => {
                    const currBook = curr.books.find(b => b.id === book.id);
                    if (currBook) {
                        return {
                            ...book,
                            quantity: {
                                ...book.quantity,
                                ...currBook.quantity
                            }
                        };
                    }
                    return book;
                });

                // 새로운 책 추가
                curr.books.forEach(book => {
                    if (!existingCurriculum.books.find(b => b.id === book.id)) {
                        existingCurriculum.books.push(book);
                    }
                });
            } else {
                acc.push(curr);
            }
            return acc;
        }, [] as OrderQuantity[]);

        return {
            deliveryData: uniqueDeliveryData,
            orderQuantities: mergedOrderQuantities
        };
    });
    const [filters, setFilters] = useState<{[key: string]: {
        categoryId: number;
        curriculumName: string;
    }}>(() => {
        // 각 배송지에 대한 초기 필터값 설정
        const initialFilters: {[key: string]: {
            categoryId: number;
            curriculumName: string;
        }} = {};

        orderData?.deliveryData?.forEach(delivery => {
            initialFilters[delivery.id] = {
                categoryId: 0,
                curriculumName: 'all'
            };
        });

        return initialFilters;
    });
    const [selectedBooks, setSelectedBooks] = useState<{[key: string]: Set<string>}>({});

    useEffect(() => {
        if (!locationState) {
            navigate('/', { replace: true });
        }
    }, [locationState, navigate]);

    // useCallback は早期リターンより前に配置（React Hooks ルール）
    const isProductAvailableForUser = useCallback((productId: number, productType: string, userId: string) => {
        const userMappings = userProductMappings?.[userId];
        // マッピング未設定（制限なし）は全商品購入可能 — OrderInput と同じロジック
        if (!userMappings || userMappings.length === 0) return true;

        return userMappings.some(mapping => {
            if (productType === 'normal') {
                return mapping.productType === 'normal' && mapping.productId === productId;
            } else if (productType === 'set') {
                return mapping.productType === 'set' && mapping.productSetId === productId;
            }
            return false;
        });
    }, [userProductMappings]);

    if (!locationState) return null;

    const handleDelete = (index: number) => {
        setSelectedDeliveryIndex(index);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (selectedDeliveryIndex !== null) {
            // 마지막 배송지 삭제 방지
            if (orderDataState.deliveryData.length <= 1) {
                setIsDeleteDialogOpen(false);
                return;
            }
            const deletedDeliveryId = orderData.deliveryData[selectedDeliveryIndex].id;
            const updatedDeliveryData = [...orderData.deliveryData];
            updatedDeliveryData.splice(selectedDeliveryIndex, 1);

            // 주문 수량 데이터에서 해당 교실의 데이터 삭제
            const updatedOrderQuantities = orderDataState.orderQuantities.map(orderQuantity => ({
                ...orderQuantity,
                books: orderQuantity.books.map(book => {
                    const { [deletedDeliveryId]: _, ...restQuantity } = book.quantity;
                    return {
                        ...book,
                        quantity: restQuantity
                    };
                }).filter(book => Object.keys(book.quantity).length > 0) // 수량이 없는 책 제거
            })).filter(orderQuantity => orderQuantity.books.length > 0); // 책이 없는 커리큘럼 제거

            // selectedBooks에서 해당 교실 데이터 삭제
            const { [deletedDeliveryId]: _, ...restSelectedBooks } = selectedBooks;

            setSelectedBooks(restSelectedBooks);
            setOrderDataState({
                deliveryData: updatedDeliveryData,
                orderQuantities: updatedOrderQuantities
            });

            navigate('/purchase/order-confirm', {
                state: {
                    orderData: {
                        deliveryData: updatedDeliveryData,
                        orderQuantities: updatedOrderQuantities
                    },
                    products,
                    productSets,
                    categories,
                    userProductMappings
                },
                replace: true
            });
        }
        setIsDeleteDialogOpen(false);
    };

    const handleDateChange = (addressId: string, date: Date | null) => {
        setDeliveryDates(prev => ({
            ...prev,
            [addressId]: date
        }));

        setOrderDataState(prev => ({
            ...prev,
            deliveryData: prev.deliveryData.map(d =>
                d.id === addressId ? { ...d, deliveryDate: date } : d
            )
        }));

        // 日付変更時に時間帯をリセットしない（既存の選択値を維持）
    };

    const handleTimeSlotChange = (addressId: string, timeSlot: number) => {
        setDeliveryTimes(prev => ({
            ...prev,
            [addressId]: timeSlot
        }));

        setOrderDataState(prev => ({
            ...prev,
            deliveryData: prev.deliveryData.map(d =>
                d.id === addressId ? { ...d, deliveryTime: timeSlot } : d
            )
        }));
    };

    const getDisabledDates = () => {
        const today = new Date();
        const disabledDate = addBusinessDays(today, 5);
        return (date: Date) => {
            return date <= disabledDate;
        };
    };

    const getOrderQuantitiesByClassroom = (classroomId: string) => {
        return orderDataState.orderQuantities.flatMap(orderQuantity =>
            orderQuantity.books
                .filter(book => {
                    // selectedBooks에 있는 경우는 새로 추가된 교재이므로 무조건 표시
                    const isNewlyAdded = selectedBooks[classroomId]?.has(`${orderQuantity.type}-${book.id}`);
                    if (isNewlyAdded) return true;

                    // 기존 데이터의 경우 quantity가 존재するものを表示（0 でも行を残す）
                    return book.quantity[classroomId] !== undefined;
                })
                .map(book => ({
                    id: book.id,
                    type: orderQuantity.type,
                    title: book.title,
                    price: book.price,
                    quantity: book.quantity[classroomId] || 0
                }))
        );
    };

    const getSubtotal = (classroomId: string) => {
        return getOrderQuantitiesByClassroom(classroomId).reduce((total, book) => total + (book.price * book.quantity), 0);
    };

    const handleDeleteOrderItem = (deliveryId: string, bookId: number, type: string) => {
        const updatedOrderQuantities = orderDataState.orderQuantities.map(orderQuantity => {
            if (orderQuantity.type === type) {
                return {
                    ...orderQuantity,
                    books: orderQuantity.books.map(book => {
                        if (book.id === bookId) {
                            // 해당 배송지의 수량만 제거
                            const { [deliveryId]: _, ...restQuantities } = book.quantity;
                            return {
                                ...book,
                                quantity: restQuantities
                            };
                        }
                        return book;
                    }).filter(book => Object.keys(book.quantity).length > 0) // 수량이 없는 도서는 제거
                };
            }
            return orderQuantity;
        }).filter(orderQuantity => orderQuantity.books.length > 0); // 도서가 없는 커리큘럼은 제거

        // selectedBooks에서 해당 교재 제거
        setSelectedBooks(prev => {
            const updated = { ...prev };
            if (updated[deliveryId]) {
                updated[deliveryId].delete(`${type}-${bookId}`);
                // Set이 비어있으면 해당 deliveryId 키 자체를 제거
                if (updated[deliveryId].size === 0) {
                    delete updated[deliveryId];
                }
            }
            return updated;
        });

        setOrderDataState({
            ...orderDataState,
            orderQuantities: updatedOrderQuantities
        });
    };

    const handleQuantityChange = (classroomId: string, bookId: number, type: string, value: number) => {
        const clamped = Math.max(0, Math.min(99999, value));
        const updatedOrderQuantities = orderDataState.orderQuantities.map(orderQuantity => {
            if (orderQuantity.type === type) {
                return {
                    ...orderQuantity,
                    books: orderQuantity.books.map(book => {
                        if (book.id === bookId) {
                            const updatedQuantity = { ...book.quantity };
                            updatedQuantity[classroomId] = clamped;
                            return {
                                ...book,
                                quantity: updatedQuantity
                            };
                        }
                        return book;
                    })
                };
            }
            return orderQuantity;
        });

        setOrderDataState({
            ...orderDataState,
            orderQuantities: updatedOrderQuantities
        });
    };

    const handleFilterChange = (deliveryId: string, field: string, value: any) => {
        setFilters(prev => {
            const deliveryFilters = prev[deliveryId] || { categoryId: 0, curriculumName: 'all' };
            if (field === 'categoryId') {
                return {
                    ...prev,
                    [deliveryId]: {
                        ...deliveryFilters,
                        [field]: value,
                        curriculumName: 'all'
                    }
                };
            } else {
                return {
                    ...prev,
                    [deliveryId]: {
                        ...deliveryFilters,
                        [field]: value
                    }
                };
            }
        });
    };

    const getFilteredCurriculums = (deliveryId: string, userId: string) => {
        const deliveryFilters = filters[deliveryId] || { categoryId: 0, curriculumName: 'all' };
        // userProductMappings はユーザーIDでキーイングされているため userId を使用
        const userMappings = userProductMappings?.[userId];
        const hasMappings = userMappings && userMappings.length > 0;

        let filteredProductCurriculums = [...new Set(products.filter(product => {
            if (deliveryFilters.categoryId && product.categoryId !== deliveryFilters.categoryId) return false;
            // 구매 가능 교재만
            if (hasMappings) {
                return userMappings.some(m => m.productType === 'normal' && m.productId === product.id);
            }
            return true;
        }).map(product => ({
            type: 'normal',
            name: product.curriculumName
        })))];

        if (!deliveryFilters.categoryId || deliveryFilters.categoryId === -1) {
            const filteredProductSetNames = [...new Set(productSets.filter(ps => {
                if (hasMappings) {
                    return userMappings.some(m => m.productType === 'set' && m.productSetId === ps.id);
                }
                return true;
            }).map(productSet => ({
                type: 'set',
                name: productSet.name
            })))];

            filteredProductCurriculums = [...filteredProductCurriculums, ...filteredProductSetNames];
        }

        return [...new Set(filteredProductCurriculums.map(item => `${item.name}-${item.type}`))].map(item => {
            const [name, type] = item.split('-');
            return { name, type };
        });
    };

    const getFilteredBooks = (deliveryId: string, userId: string) => {
        const deliveryFilters = filters[deliveryId] || { categoryId: 0, curriculumName: 'all' };

        if (!deliveryFilters.curriculumName || deliveryFilters.curriculumName === 'all') {
            return [];
        } else {
            const [name, type] = deliveryFilters.curriculumName.split('-');

            // 현재 테이블에 있는 모든 도서 ID를 가져옵니다
            const existingBooks = getOrderQuantitiesByClassroom(deliveryId).map(item => `${item.type}-${item.id}`);

            if (type === 'normal') {
                return products
                    .filter(product =>
                        product.curriculumName === name &&
                        !selectedBooks[deliveryId]?.has(`normal-${product.id}`) &&
                        !existingBooks.includes(`normal-${product.id}`) &&
                        isProductAvailableForUser(product.id, 'normal', userId)
                    )
                    .map(product => ({
                        id: product.id,
                        type: 'normal',
                        title: `${product.curriculumName || ''} ${product.volume || ''} ${product.title || ''}`
                    }));
            } else {
                return productSets
                    .filter(productSet =>
                        productSet.name === name &&
                        !selectedBooks[deliveryId]?.has(`set-${productSet.id}`) &&
                        !existingBooks.includes(`set-${productSet.id}`) &&
                        isProductAvailableForUser(productSet.id, 'set', userId)
                    )
                    .map(productSet => ({
                        id: productSet.id,
                        type: 'set',
                        title: productSet.name
                    }));
            }
        }
    };

    const convertSchoolType = (type: string): string => {
        const schoolTypeMap: { [key: string]: string } = {
            'KINDERGARTEN': i18next.t('product.kindergarten'),
            'ELEMENTARY': i18next.t('product.elementary_school'),
            'MIDDLE': i18next.t('product.middle_school'),
            'HIGH': i18next.t('product.high_school')
        };
        return schoolTypeMap[type] || type;
    };

    // isProductAvailableForUser は早期リターン前に定義済み

    const handleBookAdd = (deliveryId: string, bookValue: string) => {
        if (!selectedBooks[deliveryId]) {
            setSelectedBooks(prev => ({
                ...prev,
                [deliveryId]: new Set([bookValue])
            }));
        } else {
            setSelectedBooks(prev => ({
                ...prev,
                [deliveryId]: new Set([...prev[deliveryId], bookValue])
            }));
        }

        const [type, bookId] = bookValue.split('-');
        const updatedOrderQuantities = [...orderDataState.orderQuantities];

        if (type === 'normal') {
            const product = products.find(p => p.id === Number(bookId));
            if (product) {
                const curriculumIndex = updatedOrderQuantities.findIndex(
                    oq => oq.type === 'normal' && oq.name === product.curriculumName
                );

                if (curriculumIndex === -1) {
                    updatedOrderQuantities.push({
                        name: product.curriculumName,
                        type: 'normal',
                        books: [{
                            id: product.id,
                            title: `${product.curriculumName || ''} ${product.volume || ''} ${product.title || ''}`,
                            price: product.price,
                            quantity: { [deliveryId]: 1 }
                        }]
                    });
                } else {
                    const bookIndex = updatedOrderQuantities[curriculumIndex].books.findIndex(
                        b => b.id === product.id
                    );

                    if (bookIndex === -1) {
                        updatedOrderQuantities[curriculumIndex].books.push({
                            id: product.id,
                            title: `${product.curriculumName || ''} ${product.volume || ''} ${product.title || ''}`,
                            price: product.price,
                            quantity: { [deliveryId]: 1 }
                        });
                    }
                }
            }
        } else {
            const productSet = productSets.find(p => p.id === Number(bookId));
            if (productSet) {
                const setIndex = updatedOrderQuantities.findIndex(
                    oq => oq.type === 'set' && oq.name === productSet.name
                );

                if (setIndex === -1) {
                    updatedOrderQuantities.push({
                        name: productSet.name,
                        type: 'set',
                        books: [{
                            id: productSet.id,
                            title: productSet.name,
                            price: productSet.price,
                            quantity: { [deliveryId]: 1 }
                        }]
                    });
                } else {
                    const bookIndex = updatedOrderQuantities[setIndex].books.findIndex(
                        b => b.id === productSet.id
                    );

                    if (bookIndex === -1) {
                        updatedOrderQuantities[setIndex].books.push({
                            id: productSet.id,
                            title: productSet.name,
                            price: productSet.price,
                            quantity: { [deliveryId]: 1 }
                        });
                    }
                }
            }
        }

        setOrderDataState({
            ...orderDataState,
            orderQuantities: updatedOrderQuantities
        });
    };

    const handleTemporarySave = async () => {
        try {
            const { viewStatusStore } = rootStore;
            viewStatusStore.setLoadingStatus({ open: true });
            const orderDataToSave = {
                deliveryData: orderDataState.deliveryData.map(d => ({
                    ...d,
                    deliveryDate: deliveryDates[d.id],
                    deliveryTime: deliveryTimes[d.id]
                })),
                orderQuantities: orderDataState.orderQuantities
            };
            await axios.post('/api/v1/orders/temp', orderDataToSave, {
                headers: { 'Content-Type': 'application/json' }
            });
            setIsCompletedSaveTemporaryOrderDialogOpen(true);
        } catch (error) {
            console.error('Failed to save temp order:', error);
        } finally {
            rootStore.viewStatusStore.setLoadingStatus({ open: false });
        }
    };

    const handleCancelOrder = (tempOrderDeleted = false) => {
        const effectiveHasTempOrder = tempOrderDeleted ? false : hasTempOrder;
        setIsCancelOrderDialogOpen(false);
        if (authStore.user.level === 'CLASSROOM_MANAGER') {
            navigate('/purchase/delivery-datetime', { state: { skipTempOrderDialog: true, hasTempOrder: effectiveHasTempOrder } });
        } else {
            navigate('/purchase/delivery-address', { state: { skipTempOrderDialog: true, hasTempOrder: effectiveHasTempOrder } });
        }
    };

    const handleCancelOrderWithDeleteTemp = async () => {
        try {
            await axios.delete('/api/v1/orders/temp');
        } catch (error) {
            console.error('Failed to delete temp order:', error);
        }
        handleCancelOrder(true);
    };

    return (
        <Container maxWidth={false} sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, md: 3 }, width: '100%' }}>
            <style>{`
                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button {
                    opacity: 1 !important;
                    height: 24px;
                }
            `}</style>
            <PurchaseStepper activeStep={3} />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 1, md: 0 } }}>
                            <Typography variant="h6" gutterBottom>
                                {i18next.t('purchase.order_confirm')}
                            </Typography>
                            {authStore.user.level !== 'CLASSROOM_MANAGER' &&
                                <Button
                                    variant="outlined"
                                    sx={{
                                        fontSize: '0.8rem',
                                        height: '28px',
                                        minWidth: '80px'
                                    }}
                                    onClick={() => navigate('/purchase/delivery-address', {
                                        state: {
                                            selectedAddressIds: orderData.deliveryData.map(d => d.addressId),
                                            prevOrderData: {
                                                deliveryData: orderData.deliveryData,
                                                orderQuantities: orderData.orderQuantities,
                                                products,
                                                productSets,
                                                categories,
                                                userProductMappings
                                            },
                                            fromOrderConfirm: true,
                                            skipTempOrderDialog: true,
                                            hasTempOrder
                                        }
                                    })}
                                >
                                    {i18next.t('purchase.add_delivery_address')}
                                </Button>
                            }
                        </Box>
                        {/* 상단 3버튼: 주문 취소 / 일시 보존 / 최종 확인 */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mt: 2 }}>
                            <Button variant="outlined" sx={{ fontSize: '0.8rem', height: '35px', minWidth: '80px' }}
                                onClick={() => setIsCancelOrderDialogOpen(true)}>
                                {i18next.t('order.order_cancelled')}
                            </Button>
                            <Button variant="outlined" sx={{ fontSize: '0.8rem', height: '35px', minWidth: '80px' }}
                                onClick={handleTemporarySave}>
                                {i18next.t('purchase.save_temporarily')}
                            </Button>
                            <Button variant="contained" sx={{ fontSize: '0.8rem', height: '35px', minWidth: '80px', backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
                                disabled={!orderDataState.orderQuantities.some(oq => oq.books.some(b => Object.values(b.quantity).some(q => q > 0)))}
                                onClick={() => navigate('/purchase/order-final-confirm', {
                                    state: {
                                        orderData: {
                                            deliveryData: orderDataState.deliveryData.map(delivery => ({
                                                ...delivery,
                                                deliveryDate: deliveryDates[delivery.id],
                                                deliveryTime: deliveryTimes[delivery.id]
                                            })),
                                            orderQuantities: orderDataState.orderQuantities.map(oq => ({
                                                ...oq,
                                                books: oq.books.map(book => ({
                                                    ...book,
                                                    quantity: Object.entries(book.quantity).reduce((acc, [key, value]) => {
                                                        if (value > 0) acc[key] = value;
                                                        return acc;
                                                    }, {} as { [key: string]: number })
                                                }))
                                            }))
                                        },
                                        products, productSets, categories, userProductMappings, hasTempOrder
                                    }
                                })}>
                                {i18next.t('purchase.final_confirm')}
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mt: '20px'
                    }}>
                        <Paper sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: 'none',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#fff'
                        }}>
                            {orderData.deliveryData.map((delivery, index) => (
                                <Box key={delivery.id} sx={{ mb: 4 }}>
                                    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                        <Table sx={{
                                            '& .MuiTableCell-root': {
                                                borderRight: '1px solid #f0f0f0',
                                                borderBottom: '1px solid #f0f0f0',
                                                padding: '0 8px',
                                                height: '40px',
                                                whiteSpace: 'nowrap'
                                            }
                                        }} size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '3% !important',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('common.number')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '200px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('order.classroom')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '300px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('address.address')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('contact.phone')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('purchase.person_in_charge')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('order.delivery_date')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('order.time_slot')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '80px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        Actions
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
                                                        {index + 1}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
                                                        {delivery.classroomName}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem' }}>
                                                        {delivery.address}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
                                                        {delivery.phone}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
                                                        {delivery.userName}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem' }}>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
                                                            <DatePicker
                                                                value={deliveryDates[delivery.id] || null}
                                                                onChange={(newValue) => handleDateChange(delivery.id, newValue)}
                                                                shouldDisableDate={getDisabledDates()}
                                                                slotProps={{
                                                                    textField: {
                                                                        inputProps: {
                                                                            readOnly: true,
                                                                        },
                                                                    },
                                                                }}
                                                                sx={{
                                                                    width: '100%',
                                                                    '& .MuiInputBase-root': {
                                                                        height: '35px',
                                                                        fontSize: '0.8rem'
                                                                    }
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem' }}>
                                                        <FormControl fullWidth>
                                                            <Select
                                                                disabled={false}
                                                                value={deliveryTimes[delivery.id] || ''}
                                                                onChange={(e) => handleTimeSlotChange(delivery.id, Number(e.target.value))}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.8rem',
                                                                    height: '35px'
                                                                }}
                                                            >
                                                                {timeSlots.map((slot) => (
                                                                    <MenuItem
                                                                        key={slot.id}
                                                                        value={slot.id}
                                                                        sx={{ fontSize: '0.8rem' }}
                                                                    >
                                                                        {slot.time}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
                                                        <Button
                                                            disabled={orderDataState.deliveryData.length <= 1}
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleDelete(index)}
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                height: '24px',
                                                                minWidth: '60px'
                                                            }}
                                                        >
                                                            {i18next.t('common.delete')}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell />
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.textbook_name')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('product.price')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.quantity')}
                                                    </TableCell>
                                                    <TableCell /><TableCell /><TableCell />
                                                </TableRow>
                                                {getOrderQuantitiesByClassroom(delivery.id).map((orderQuantity, index) => {
                                                    return (
                                                        <TableRow key={`${orderQuantity.type}-${orderQuantity.id}`}>
                                                            <TableCell />
                                                            <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                                {index + 1}
                                                            </TableCell>
                                                            <TableCell colSpan={1} sx={{ fontSize: '0.7rem' }}>
                                                                {orderQuantity.title}
                                                            </TableCell>
                                                            <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                                {i18next.t('common.price_yen', { price: orderQuantity.price })}
                                                            </TableCell>
                                                            <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                                <TextField
                                                                    type="number"
                                                                    value={orderQuantity.quantity || ''}
                                                                    onChange={(e) => handleQuantityChange(
                                                                        delivery.id,
                                                                        orderQuantity.id,
                                                                        orderQuantity.type,
                                                                        parseInt(e.target.value) || 0
                                                                    )}
                                                                    onFocus={(e) => {
                                                                        if ((e.target as HTMLInputElement).value === '0') (e.target as HTMLInputElement).value = '';
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        const val = parseInt((e.target as HTMLInputElement).value) || 0;
                                                                        handleQuantityChange(delivery.id, orderQuantity.id, orderQuantity.type, val);
                                                                    }}
                                                                    disabled={!isProductAvailableForUser(orderQuantity.id, orderQuantity.type, delivery.userId ?? delivery.id)}
                                                                    InputProps={{
                                                                        inputProps: {
                                                                            min: 0,
                                                                            max: 99999,
                                                                            style: {
                                                                                textAlign: 'center',
                                                                                fontSize: '0.7rem',
                                                                                padding: '4px'
                                                                            }
                                                                        }
                                                                    }}
                                                                    size="small"
                                                                    sx={{
                                                                        width: '60px',
                                                                        '& .MuiOutlinedInput-root': {
                                                                            height: '28px'
                                                                        },
                                                                        '& .Mui-disabled': {
                                                                            backgroundColor: '#f5f5f5',
                                                                            color: '#999'
                                                                        }
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                                {i18next.t('common.price_yen', { price: orderQuantity.price * orderQuantity.quantity })}
                                                            </TableCell>
                                                            <TableCell />
                                                            <TableCell sx={{ textAlign: 'center' }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    size="small"
                                                                    disabled={!isProductAvailableForUser(orderQuantity.id, orderQuantity.type, delivery.userId ?? delivery.id)}
                                                                    onClick={() => handleDeleteOrderItem(delivery.id, orderQuantity.id, orderQuantity.type)}
                                                                    sx={{
                                                                        fontSize: '0.7rem',
                                                                        height: '24px',
                                                                        minWidth: '60px',
                                                                        '&.Mui-disabled': {
                                                                            backgroundColor: '#f5f5f5',
                                                                            color: '#999'
                                                                        }
                                                                    }}
                                                                >
                                                                    {i18next.t('common.delete')}
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                                <TableRow>
                                                    <TableCell />
                                                    <TableCell />
                                                    <TableCell sx={{height: '20px !important'}}>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                                                <Select
                                                                    value={filters[delivery.id]?.curriculumName}
                                                                    onChange={(e: SelectChangeEvent<string>) => handleFilterChange(delivery.id, 'curriculumName', e.target.value)}
                                                                    sx={{ height: '35px', fontSize: '0.8rem' }}
                                                                >
                                                                    <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>
                                                                        {i18next.t('product.curriculum_name')}
                                                                    </MenuItem>
                                                                    {getFilteredCurriculums(delivery.id, delivery.userId ?? delivery.id).map((curriculum) => (
                                                                        <MenuItem
                                                                            key={`${curriculum.type}-${curriculum.name}`}
                                                                            value={`${curriculum.name}-${curriculum.type}`}
                                                                            sx={{ fontSize: '0.8rem' }}
                                                                        >
                                                                            {curriculum.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <FormControl size="small" sx={{ minWidth: 300 }}>
                                                                <Select
                                                                    value=""
                                                                    onChange={(e: SelectChangeEvent<string>) => handleBookAdd(delivery.id, e.target.value)}
                                                                    sx={{ height: '35px', fontSize: '0.8rem' }}
                                                                    disabled={!filters[delivery.id]?.curriculumName || filters[delivery.id]?.curriculumName === 'all'}
                                                                >
                                                                    <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                                                                        {i18next.t('order.textbook_name')}
                                                                    </MenuItem>
                                                                    {getFilteredBooks(delivery.id, delivery.userId ?? delivery.id)
                                                                        .filter(book => !selectedBooks[delivery.id]?.has(`${book.type}-${book.id}`))
                                                                        .map((book) => (
                                                                            <MenuItem
                                                                                key={`${book.type}-${book.id}`}
                                                                                value={`${book.type}-${book.id}`}
                                                                                sx={{ fontSize: '0.8rem' }}
                                                                            >
                                                                                {book.title}
                                                                            </MenuItem>
                                                                        ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.subtotal')}
                                                    </TableCell>
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('common.price_yen', { price: getSubtotal(delivery.id) })}
                                                    </TableCell>
                                                    <TableCell /><TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('user.shipping_fee')}
                                                    </TableCell>
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('common.price_yen', { price: (getSubtotal(delivery.id) === 0 ? 0 : delivery.postage) })}
                                                    </TableCell>
                                                    <TableCell /><TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.tax')}
                                                    </TableCell>
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('common.price_yen', { price: Math.floor((getSubtotal(delivery.id) + (getSubtotal(delivery.id) === 0 ? 0 : delivery.postage)) * appConfigStore.taxRate) })}
                                                    </TableCell>
                                                    <TableCell /><TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center', fontWeight: 'bold' }}>
                                                        {i18next.t('order.total')}
                                                    </TableCell>
                                                    <TableCell colSpan={1} sx={{ fontSize: '0.7rem', textAlign: 'center', fontWeight: 'bold' }}>
                                                        {(() => {
                                                            const sub = getSubtotal(delivery.id);
                                                            const shipping = sub === 0 ? 0 : delivery.postage;
                                                            const tax = Math.floor((sub + shipping) * appConfigStore.taxRate);
                                                            return i18next.t('common.price_yen', { price: sub + shipping + tax });
                                                        })()}
                                                    </TableCell>
                                                    <TableCell /><TableCell />
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}
                        </Paper>
                    </Box>

                    <Box sx={{
                        p: 1,
                        backgroundColor: '#fff',
                        borderTop: '1px solid #e0e0e0',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 1,
                        minHeight: '35px',
                        flexShrink: 0
                    }}>
                        <Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
                            <Button
                                variant="outlined"
                                sx={{
                                    fontSize: '0.8rem',
                                    height: '28px',
                                    minWidth: '80px',
                                    mr: { xs: 0, md: 1 },
                                    width: { xs: '100%', md: 'auto' }
                                }}
                                onClick={() => navigate('/purchase/order-input', {
                                    state: {
                                        deliveryData: orderDataState.deliveryData.map(delivery => ({
                                            ...delivery,
                                            deliveryDate: deliveryDates[delivery.id],
                                            deliveryTime: deliveryTimes[delivery.id]
                                        })),
                                        prevOrderData: {
                                            selectedCurriculums: orderDataState.orderQuantities.map(orderQuantity => {
                                                // 커리큘럼의 모든 책이 선택되지 않았는지 확인
                                                const hasAnyBookSelected = orderQuantity.books.some(book =>
                                                    Object.values(book.quantity).some(qty => qty > 0)
                                                );

                                                // 커리큘럼에 선택된 교재가 하나도 없으면 null 반환
                                                if (!hasAnyBookSelected) {
                                                    return null;
                                                }

                                                // 해당 커리큘럼의 모든 교재 가져오기
                                                let allBooks;
                                                if (orderQuantity.type === 'normal') {
                                                    allBooks = products
                                                        .filter(product => product.curriculumName === orderQuantity.name)
                                                        .map(product => ({
                                                            id: product.id,
                                                            title: `${product.curriculumName || ''} ${product.volume || ''} ${product.title || ''}`,
                                                            price: product.price,
                                                            quantity: orderDataState.deliveryData.reduce((acc, delivery) => ({
                                                                ...acc,
                                                                [delivery.id]: 0
                                                            }), {}),
                                                            isSelected: false
                                                        }));
                                                } else {
                                                    allBooks = productSets
                                                        .filter(productSet => productSet.name === orderQuantity.name)
                                                        .map(productSet => ({
                                                            id: productSet.id,
                                                            title: productSet.name,
                                                            price: productSet.price,
                                                            quantity: orderDataState.deliveryData.reduce((acc, delivery) => ({
                                                                ...acc,
                                                                [delivery.id]: 0
                                                            }), {}),
                                                            isSelected: false
                                                        }));
                                                }

                                                // 기존 수량 데이터 병합
                                                const mergedBooks = allBooks.map(book => {
                                                    const existingBook = orderQuantity.books.find(b => b.id === book.id);
                                                    if (existingBook) {
                                                        // 모든 교실에 대한 quantity 초기화
                                                        const initializedQuantity = orderDataState.deliveryData.reduce((acc, delivery) => ({
                                                            ...acc,
                                                            [delivery.id]: existingBook.quantity[delivery.id] || 0
                                                        }), {});

                                                        return {
                                                            ...book,
                                                            quantity: initializedQuantity,
                                                            isSelected: Object.values(existingBook.quantity).some(qty => qty > 0)
                                                        };
                                                    }
                                                    return null; // 기존 데이터가 없는 경우 제거
                                                }).filter(Boolean); // null 값 제거

                                                return {
                                                    name: orderQuantity.name,
                                                    type: orderQuantity.type,
                                                    books: mergedBooks
                                                };
                                            }).filter(Boolean), // null 값(수량이 0인 커리큘럼) 제거
                                            filters: {
                                                categoryId: 0
                                            }
                                        },
                                        userProductMappings,
                                        hasTempOrder
                                    }
                                })}
                            >
                                {i18next.t('purchase.go_to_order_input')}
                            </Button>
                        </Box>
                        <Box sx={{
                            p: { xs: 0, md: 1 },
                            backgroundColor: '#fff',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'flex-end',
                            alignItems: { xs: 'stretch', md: 'center' },
                            gap: 1,
                            minHeight: '35px',
                            flexShrink: 0
                        }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', justifyContent: 'flex-end', mt: 2 }}>
                                <Button variant="outlined" sx={{ fontSize: '0.8rem', height: '28px', minWidth: '80px' }}
                                    onClick={() => setIsCancelOrderDialogOpen(true)}>
                                    {i18next.t('order.order_cancelled')}
                                </Button>
                                <Button variant="outlined" sx={{ fontSize: '0.8rem', height: '28px', minWidth: '80px' }}
                                    onClick={handleTemporarySave}>
                                    {i18next.t('purchase.save_temporarily')}
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!orderDataState.orderQuantities.some(oq =>
                                        oq.books.some(b => Object.values(b.quantity).some(q => q > 0))
                                    )}
                                    sx={{
                                        fontSize: '0.8rem',
                                        height: '28px',
                                        minWidth: '80px',
                                        backgroundColor: 'primary.main',
                                        '&:hover': { backgroundColor: 'primary.dark' }
                                    }}
                                    onClick={() => {
                                        navigate('/purchase/order-final-confirm', {
                                            state: {
                                                orderData: {
                                                    deliveryData: orderDataState.deliveryData.map(delivery => ({
                                                        ...delivery,
                                                        deliveryDate: deliveryDates[delivery.id],
                                                        deliveryTime: deliveryTimes[delivery.id]
                                                    })),
                                                    orderQuantities: orderDataState.orderQuantities.map(orderQuantity => ({
                                                        ...orderQuantity,
                                                        books: orderQuantity.books.map(book => ({
                                                            ...book,
                                                            quantity: Object.entries(book.quantity).reduce((acc, [key, value]) => {
                                                                if (value > 0) {
                                                                    acc[key] = value;
                                                                }
                                                                return acc;
                                                            }, {} as { [key: string]: number })
                                                        }))
                                                    }))
                                                },
                                                products,
                                                productSets,
                                                categories,
                                                userProductMappings,
                                                hasTempOrder
                                            }
                                        });
                                    }}
                                >
                                    {i18next.t('purchase.final_confirm')}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <ConfirmDialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
                title={i18next.t('common.delete')}
                message={i18next.t('common.confirm_delete')}
                button1Text={i18next.t('common.cancel')}
                button2Text={i18next.t('common.delete')}
                confirmColor="error"
            />
            <ConfirmDialog
                open={isCancelOrderDialogOpen}
                onClose={() => setIsCancelOrderDialogOpen(false)}
                onConfirm={handleCancelOrder}
                title={i18next.t('order.order_cancelled')}
                message={i18next.t('common.confirm_cancel')}
        				button1Text={i18next.t('common.no')}
				        button2Text={i18next.t('common.yes')}
                onConfirmAlt={hasTempOrder ? handleCancelOrderWithDeleteTemp : undefined}
                confirmAltText={hasTempOrder ? i18next.t('purchase.yes_delete_temp_order') : undefined}
                confirmAltColor="error"
            />
            <ConfirmDialog
                open={isCompletedSaveTemporaryOrderDialogOpen}
                onClose={null}
                onConfirm={() => setIsCompletedSaveTemporaryOrderDialogOpen(false)}
                title={i18next.t('purchase.save_temporarily')}
                message={i18next.t('purchase.saved_successfully')}
                button1Text={null}
                button2Text={i18next.t('common.confirm')}
            />
        </Container>
    );
};

export default OrderConfirm;